/**
 * @file Photo Type Configuration RTK Query API
 * @description 로딩 사진 타입 설정 API 엔드포인트
 * @see docs/plans/loading/05-implementation.md - Section 31.2
 */

import { apiSlice } from '../baseQuery';
import type { Row } from '../type';
import type {
  GetPhotoTypeConfigResponse,
  UpdatePhotoTypeConfigRequest,
} from './interface';

/**
 * PhotoTypeConfig 태그 추가
 */
const apiWithTag = apiSlice.enhanceEndpoints({
  addTagTypes: ['PhotoTypeConfig'],
});

/**
 * Photo Type API 엔드포인트
 */
export const photoTypeApi = apiWithTag.injectEndpoints({
  endpoints: (build) => ({
    /**
     * 사진 타입 설정 조회
     *
     * @description 현재 수출자의 사진 타입 설정 목록을 조회합니다.
     * @endpoint GET /exporters/this/photo-type-configs
     */
    getPhotoTypeConfig: build.query<GetPhotoTypeConfigResponse, void>({
      query: () => ({
        url: '/exporters/this/photo-type-configs',
      }),
      providesTags: ['PhotoTypeConfig'],
      keepUnusedDataFor: 300, // 5분 캐시 유지
      transformResponse: (res: Row<GetPhotoTypeConfigResponse>) => res.row,
    }),

    /**
     * 사진 타입 설정 수정
     *
     * @description 사진 타입 설정을 일괄 수정합니다.
     * - 최소 7개, 최대 15개 슬롯 필요
     * - 기본 유형은 중복 선택 불가
     * @endpoint PUT /exporters/this/photo-type-configs
     */
    updatePhotoTypeConfig: build.mutation<
      GetPhotoTypeConfigResponse,
      UpdatePhotoTypeConfigRequest
    >({
      query: (body) => ({
        method: 'PUT',
        url: '/exporters/this/photo-type-configs',
        body,
      }),
      invalidatesTags: ['PhotoTypeConfig'],
      transformResponse: (res: Row<GetPhotoTypeConfigResponse>) => res.row,
    }),

    /**
     * 사진 타입 설정 초기화
     *
     * @description 사진 타입 설정을 기본값으로 초기화합니다.
     * @endpoint POST /exporters/this/photo-type-configs/reset
     */
    resetPhotoTypeConfig: build.mutation<GetPhotoTypeConfigResponse, void>({
      query: () => ({
        method: 'POST',
        url: '/exporters/this/photo-type-configs/reset',
      }),
      invalidatesTags: ['PhotoTypeConfig'],
      transformResponse: (res: Row<GetPhotoTypeConfigResponse>) => res.row,
    }),
  }),
});

/**
 * Auto-generated React hooks
 */
export const {
  useGetPhotoTypeConfigQuery,
  useLazyGetPhotoTypeConfigQuery,
  useUpdatePhotoTypeConfigMutation,
  useResetPhotoTypeConfigMutation,
} = photoTypeApi;
